import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { readData } from '@/firebase/database';

interface LoadingScreenProps {
  duration?: number;
  enabled?: boolean;
  onComplete: () => void;
}

const LoadingScreen = ({ duration = 3000, enabled = true, onComplete }: LoadingScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(enabled);
  const [brandName, setBrandName] = useState('Mustakim.dev');
  const [slogan, setSlogan] = useState('Initializing Experience');

  // Load editable text from Firebase
  useEffect(() => {
    readData('settings').then((s) => {
      if (s?.loadingBrandName) setBrandName(s.loadingBrandName as string);
      if (s?.loadingSlogan) setSlogan(s.loadingSlogan as string);
    });
  }, []);

  useEffect(() => {
    if (!enabled) { onComplete(); return; }
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) { clearInterval(interval); return 100; }
        return p + 100 / (duration / 50);
      });
    }, 50);
    return () => clearInterval(interval);
  }, [duration, enabled, onComplete]);

  useEffect(() => {
    if (progress >= 100) {
      const t = setTimeout(() => { setVisible(false); onComplete(); }, 600);
      return () => clearTimeout(t);
    }
  }, [progress, onComplete]);

  if (!enabled) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: 'hsl(var(--background))' }}
        >
          {/* Grid background */}
          <div className="absolute inset-0 grid-bg opacity-20" />

          {/* Radial glow */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at center, hsl(var(--primary) / 0.08) 0%, transparent 60%)',
          }} />

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative mb-8"
          >
            <div className="w-20 h-20 rounded-2xl border border-primary/30 flex items-center justify-center relative overflow-hidden">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-0 rounded-2xl"
                style={{ background: 'conic-gradient(from 0deg, transparent, hsl(var(--primary) / 0.15), transparent)' }}
              />
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
              </svg>
            </div>
          </motion.div>

          {/* Name */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="font-display text-2xl sm:text-3xl font-bold mb-3"
            style={{ color: 'hsl(var(--foreground))' }}
          >
            <span className="gradient-text">{brandName}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 0.5 }}
            className="font-mono text-xs tracking-[0.3em] uppercase mb-10"
            style={{ color: 'hsl(var(--muted-foreground))' }}
          >
            {slogan}
          </motion.p>

          {/* Progress bar */}
          <div className="w-48 sm:w-64">
            <div className="h-[2px] rounded-full overflow-hidden" style={{ background: 'hsl(var(--border))' }}>
              <motion.div
                className="h-full rounded-full"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.5))',
                  boxShadow: '0 0 10px hsl(var(--primary) / 0.4)',
                }}
              />
            </div>
            <div className="flex justify-between mt-2">
              <span className="font-mono text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
                Loading
              </span>
              <span className="font-mono text-[10px]" style={{ color: 'hsl(var(--primary))' }}>
                {Math.round(progress)}%
              </span>
            </div>
          </div>

          {/* Corner decorations */}
          <div className="absolute top-6 left-6 w-8 h-8 border-l border-t border-primary/20" />
          <div className="absolute top-6 right-6 w-8 h-8 border-r border-t border-primary/20" />
          <div className="absolute bottom-6 left-6 w-8 h-8 border-l border-b border-primary/20" />
          <div className="absolute bottom-6 right-6 w-8 h-8 border-r border-b border-primary/20" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
